import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storageService } from '../services/storageService';
import { Property, Tenant, Deal, MaintenanceTask } from '../types';

export const QUERY_KEYS = {
  properties: 'properties',
  tenants: 'tenants',
  deals: 'deals',
  tasks: 'tasks', // Novo
  allData: 'allData',
};

// --- PROPERTIES ---
export const usePropertiesQuery = (userId: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.properties, userId],
    queryFn: async () => {
      if (!userId) return [];
      return await storageService.getProperties(userId);
    },
    enabled: !!userId,
  });
};

export const useAddPropertyMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newProp: Omit<Property, 'id'>) => {
      if (!userId) throw new Error("No user");
      return storageService.addProperty(userId, newProp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.properties, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allData, userId] });
    },
  });
};

export const useUpdatePropertyMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prop: Property) => {
      if (!userId) throw new Error("No user");
      return storageService.updateProperty(userId, prop);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.properties, userId] });
    },
  });
};

export const useDeletePropertyMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (propId: string) => {
      if (!userId) throw new Error("No user");
      return storageService.deleteProperty(userId, propId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.properties, userId] });
    },
  });
};

// --- TENANTS ---
export const useTenantsQuery = (userId: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.tenants, userId],
    queryFn: async () => {
      if (!userId) return [];
      return await storageService.getTenants(userId);
    },
    enabled: !!userId,
  });
};

export const useAddTenantMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newTenant: Omit<Tenant, 'id'>) => {
      if (!userId) throw new Error("No user");
      return storageService.addTenant(userId, newTenant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.tenants, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allData, userId] });
    },
  });
};

export const useUpdateTenantMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenant: Tenant) => {
      if (!userId) throw new Error("No user");
      return storageService.updateTenant(userId, tenant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.tenants, userId] });
    },
  });
};

export const useDeleteTenantMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => {
      if (!userId) throw new Error("No user");
      return storageService.deleteTenant(userId, tenantId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.tenants, userId] });
    },
  });
};

// --- DEALS ---
export const useDealsQuery = (userId: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.deals, userId],
    queryFn: async () => {
      if (!userId) return [];
      return await storageService.getDeals(userId);
    },
    enabled: !!userId,
  });
};

export const useAddDealMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newDeal: Omit<Deal, 'id' | 'dateAdded'>) => {
      if (!userId) throw new Error("No user");
      return storageService.addDeal(userId, newDeal);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deals, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allData, userId] });
    },
  });
};

export const useUpdateDealMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deal: Deal) => {
      if (!userId) throw new Error("No user");
      return storageService.updateDeal(userId, deal);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deals, userId] });
    },
  });
};

export const useDeleteDealMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dealId: string) => {
      if (!userId) throw new Error("No user");
      return storageService.deleteDeal(userId, dealId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deals, userId] });
    },
  });
};

// --- TASKS (NOVO) ---
export const useTasksQuery = (userId: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.tasks, userId],
    queryFn: async () => {
      if (!userId) return [];
      return await storageService.getTasks(userId);
    },
    enabled: !!userId,
  });
};

export const useAddTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newTask: Omit<MaintenanceTask, 'id' | 'dateCreated'>) => {
      if (!userId) throw new Error("No user");
      return storageService.addTask(userId, newTask);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.tasks, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allData, userId] });
    },
  });
};

export const useUpdateTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (task: MaintenanceTask) => {
      if (!userId) throw new Error("No user");
      return storageService.updateTask(userId, task);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.tasks, userId] });
    },
  });
};

export const useDeleteTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => {
      if (!userId) throw new Error("No user");
      return storageService.deleteTask(userId, taskId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.tasks, userId] });
    },
  });
};

// --- LEGACY ---
export const useAppData = (userId: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.allData, userId],
    queryFn: async () => {
      if (!userId) throw new Error("No user ID");
      return await storageService.getUserData(userId);
    },
    enabled: !!userId,
  });
};

export const useAppMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const { data: currentData } = useAppData(userId);

  return useMutation({
    mutationFn: async (newData: any) => {
      if (!userId || !currentData) return;
      const mergedData = { ...currentData, ...newData };
      await storageService.saveData(userId, mergedData);
      return mergedData;
    },
    onSuccess: (newData) => {
      queryClient.setQueryData([QUERY_KEYS.allData, userId], newData);
    },
  });
};